'use client';

import { useTranslations, useLocale } from 'next-intl';
import React, { useCallback, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import rehypeRaw from 'rehype-raw';

import { useDomainConfiguration } from '@/components/DomainConfigurationProvider';

import { copyToClipboard } from '@cineamo/ui/src/helpers/clipboard-helper';

import { IconShareBox24 } from '@cineamo/ui/src/icons/ic-share-box';
import ImageGallery from '@cineamo/ui/src/image/ImageGallery';

import { useAnalytics } from '@cineamo/composables/src/providers/analyticsProvider';

import CompetitionHtmlEmbed from './CompetitionHtmlEmbed';
import SectionHeader from './SectionHeader';

export const COMPETITION_ANCHOR_ID = 'contest';

function CompetitionMDComponents(primaryColor: string): Partial<Components> {
    return {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        p: ({ node, ...props }) => (
            <p
                {...props}
                className="text-[14px] leading-relaxed whitespace-pre-wrap"
            />
        ),
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        a: ({ node, ...props }) => (
            <a
                {...props}
                className="font-bold underline"
                style={{ color: primaryColor }}
                target="_blank"
                rel="noopener noreferrer"
            />
        ),
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        strong: ({ node, ...props }) => (
            <strong
                {...props}
                className="font-bold"
            />
        ),
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        ul: ({ node, ...props }) => (
            <ul
                {...props}
                className="list-disc pl-[12px]"
            />
        ),
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        ol: ({ node, ...props }) => (
            <ol
                {...props}
                className="list-decimal pl-[12px]"
            />
        ),
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        li: ({ node, ...props }) => (
            <li
                {...props}
                className="ml-[16px] text-[14px] whitespace-pre-wrap"
            />
        )
    };
}

const CompetitionSection: React.FC = () => {
    const t = useTranslations();
    const locale = useLocale();
    const { domainConfiguration } = useDomainConfiguration();
    const { track } = useAnalytics();
    const [imageGalleryOpen, setImageGalleryOpen] = useState(false);

    const competition = domainConfiguration.competition?.[locale];
    const { textTop, textBottom, html, imageUrlDesktop, imageUrlMobile, buttonText, buttonUrl } = competition ?? {};

    // Shares the contest itself rather than the page, so the link lands on the section instead of
    // the hero. Built at click time — on the server there is no origin to build it from.
    const shareCompetition = useCallback(async () => {
        const link = `${window.location.origin}${window.location.pathname}#${COMPETITION_ANCHOR_ID}`;
        const shareData = { url: link };
        if (!navigator.canShare?.(shareData)) {
            copyToClipboard(link, t('showing-info.copied'));
            return;
        }
        track('Contest Shared', { content_type: 'contest' });
        await navigator.share(shareData).catch(() => {});
    }, [t, track]);

    if (!textTop && !textBottom && !html && !imageUrlDesktop && !imageUrlMobile && !buttonText) {
        return null;
    }
    const primaryColor = domainConfiguration.style?.primaryColor || '#00A8FF';
    const onPrimaryColor = domainConfiguration.style?.onPrimaryColor || '#FFFFFF';

    return (
        <section id={COMPETITION_ANCHOR_ID}>
            <SectionHeader
                title={t('competition.title')}
                action={
                    <button
                        type="button"
                        onClick={() => shareCompetition()}
                        className="inline-flex items-center gap-8 px-16 py-10 rounded-[6px] bg-white/10 backdrop-blur-[12px] text-body-bold-16 cursor-pointer transition-opacity hover:opacity-80">
                        <IconShareBox24 aria-hidden="true" />
                        {t('common.share')}
                    </button>
                }
            />

            <div className="flex flex-col gap-24">
                {/* Top text block */}
                {textTop && (
                    <div className="text-white">
                        <ReactMarkdown
                            rehypePlugins={[rehypeRaw]}
                            components={CompetitionMDComponents(primaryColor)}>
                            {textTop}
                        </ReactMarkdown>
                    </div>
                )}

                {/* Clickable image — separate desktop/mobile */}
                {(imageUrlDesktop || imageUrlMobile) && (
                    <>
                        <div
                            className="cursor-pointer overflow-hidden rounded-16"
                            onClick={() => setImageGalleryOpen(true)}>
                            {imageUrlDesktop && (
                                // eslint-disable-next-line @next/next/no-img-element
                                <img
                                    src={imageUrlDesktop}
                                    alt={t('competition.title')}
                                    className={`w-full transition-transform hover:scale-105 ${imageUrlMobile ? 'hidden lg:block' : ''}`}
                                />
                            )}
                            {imageUrlMobile && (
                                // eslint-disable-next-line @next/next/no-img-element
                                <img
                                    src={imageUrlMobile}
                                    alt={t('competition.title')}
                                    className={`w-full transition-transform hover:scale-105 ${imageUrlDesktop ? 'lg:hidden' : ''}`}
                                />
                            )}
                        </div>
                        {imageGalleryOpen && (
                            <ImageGallery
                                images={[imageUrlDesktop || imageUrlMobile].filter(Boolean) as string[]}
                                open={true}
                                onClose={() => setImageGalleryOpen(false)}
                                currentIndex={0}
                            />
                        )}
                    </>
                )}

                {/* Operator-supplied embed, e.g. the entry form */}
                {html && <CompetitionHtmlEmbed html={html} />}

                {/* Bottom text block */}
                {textBottom && (
                    <div className="text-white/70 italic text-[12px]">
                        <ReactMarkdown
                            rehypePlugins={[rehypeRaw]}
                            components={CompetitionMDComponents(primaryColor)}>
                            {textBottom}
                        </ReactMarkdown>
                    </div>
                )}

                {/* CTA Button */}
                {buttonText && buttonUrl && (
                    <div className="flex justify-center">
                        <a
                            href={buttonUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center px-32 py-12 rounded-8 text-body-bold-16 transition-opacity hover:opacity-90"
                            style={{
                                backgroundColor: primaryColor,
                                color: onPrimaryColor
                            }}>
                            {buttonText}
                        </a>
                    </div>
                )}
            </div>
        </section>
    );
};

export default CompetitionSection;
